import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import Reveal from './Reveal';

const KENYAN_PHONE_RE = /^(?:254|0)(7|1)\d{8}$/;

function QuickPay() {
  const [propertyGroups, setPropertyGroups] = useState([]); // [{ property_id, property_name, units: [...] }]
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/properties`);
        const props = await res.json();
        if (!res.ok || !Array.isArray(props) || props.length === 0) return;

        // Fetch every property's houses up front — a dropdown labeled
        // "House N · Property" stays unambiguous even once there's more
        // than one property, unlike matching on a typed-in house number.
        const groups = await Promise.all(
          props.map(async (p) => {
            try {
              const unitsRes = await fetch(`${API_BASE}/api/properties/${p.property_id}/units/public`);
              const unitsData = await unitsRes.json();
              return {
                property_id: p.property_id,
                property_name: p.name,
                units: unitsRes.ok ? (unitsData.units || []) : [],
              };
            } catch (err) {
              return { property_id: p.property_id, property_name: p.name, units: [] };
            }
          })
        );
        setPropertyGroups(groups.filter((g) => g.units.length > 0));
      } catch (err) {
        // leave the form as-is — the full /pay page still works as a fallback
      }
    }
    load();
  }, []);

  const hasMultipleProperties = propertyGroups.length > 1;

  async function pollStatus(checkoutRequestId) {
    const maxAttempts = 30; // ~90s, matches how long an STK prompt stays live
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const res = await fetch(`${API_BASE}/api/mpesa/status/${checkoutRequestId}`);
        const data = await res.json();

        if (data.status === 'success' && data.receipt) {
          const r = data.receipt;
          window.location.href =
            `/success?receipt=${r.receipt_no}` +
            `&payment_id=${r.payment_id}` +
            `&invoice_no=${encodeURIComponent(r.invoice_no || '')}` +
            `&unit=${r.unit_number}` +
            `&tenant=${encodeURIComponent(r.tenant_name)}` +
            `&amount=${r.amount_paid}` +
            `&rent=${r.rent_amount}` +
            `&penalty=${r.penalty}` +
            `&balance_remaining=${r.balance_remaining}` +
            `&mpesa=${r.mpesa_code}` +
            `&month=${encodeURIComponent(r.month)}` +
            `&date=${encodeURIComponent(r.payment_date)}`;
          return;
        }
        if (data.status === 'failed') {
          setError(data.result_desc || 'Payment was not completed. Please try again.');
          setStatus('');
          setSubmitting(false);
          return;
        }
        setStatus('Waiting for you to complete the payment on your phone...');
      } catch (err) {
        // transient network hiccup while polling — keep trying until maxAttempts
      }
    }
    setError('This is taking longer than expected. If you entered your PIN, check back shortly.');
    setStatus('');
    setSubmitting(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const cleanPhone = phone.replace(/\s/g, '');
    if (!KENYAN_PHONE_RE.test(cleanPhone)) {
      setError('Enter a valid Kenyan number, e.g. 0712 345 678');
      return;
    }

    if (!selectedUnitId) {
      setError('Select your house.');
      return;
    }
    const unitId = parseInt(selectedUnitId, 10);

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }

    setError('');
    setSubmitting(true);
    setStatus('Sending the request to your phone...');

    try {
      const res = await fetch(`${API_BASE}/api/mpesa/stkpush`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unit_id: unitId, phone: cleanPhone, amount: parsedAmount }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Payment failed. Please try again.');
        setStatus('');
        setSubmitting(false);
        return;
      }

      setStatus('Check your phone and enter your M-Pesa PIN to complete the payment.');
      pollStatus(data.checkout_request_id);
    } catch (err) {
      setError('Could not reach the server. Please try again.');
      setStatus('');
      setSubmitting(false);
    }
  }

  return (
    <section id="quick-pay" style={styles.section}>
      <Reveal style={styles.card} className="card-lift">
        <div style={styles.header}>
          <h2 style={styles.title}>Quick Pay</h2>
          <p style={styles.subtitle}>
            Pay your rent in under a minute — no login needed.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="qp-house">House</label>
            <select
              id="qp-house"
              className="input-field"
              style={styles.input}
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
            >
              <option value="">Select your house...</option>
              {propertyGroups.map((group) => (
                hasMultipleProperties ? (
                  <optgroup key={group.property_id} label={group.property_name}>
                    {group.units.map((u) => (
                      <option key={u.unit_id} value={u.unit_id}>
                        House {u.unit_number}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  group.units.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>
                      House {u.unit_number} · {group.property_name}
                    </option>
                  ))
                )
              ))}
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="qp-phone">M-Pesa Phone Number</label>
            <input
              id="qp-phone"
              className="input-field"
              style={styles.input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0712 345 678"
              type="tel"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="qp-amount">Amount (Ksh)</label>
            <input
              id="qp-amount"
              className="input-field"
              style={styles.input}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 5000"
              type="number"
              min="1"
            />
          </div>
          <button type="submit" disabled={submitting} style={styles.submitBtn} className="btn-lift">
            {submitting ? 'Sending...' : 'Send M-Pesa Prompt'}
          </button>
        </form>

        {status !== '' && <p style={styles.status}>📱 {status}</p>}
        {error !== '' && <p style={styles.error}>{error}</p>}
      </Reveal>
    </section>
  );
}

const styles = {
  section: {
    padding: '0 24px 64px',
  },
  card: {
    maxWidth: '1200px',
    margin: '-48px auto 0',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    padding: '28px 32px',
    position: 'relative',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '20px',
    fontWeight: 800,
    color: 'var(--color-ink)',
    margin: '0 0 4px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-ink-soft)',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'flex-end',
  },
  field: {
    flex: '1 1 160px',
    minWidth: '140px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-ink-soft)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '15px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-ink)',
  },
  submitBtn: {
    flex: '1 1 200px',
    padding: '13px 20px',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  status: {
    marginTop: '16px',
    marginBottom: 0,
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-primary-light)',
    color: 'var(--color-primary-dark)',
    fontSize: '13px',
    fontWeight: 500,
  },
  error: {
    marginTop: '16px',
    marginBottom: 0,
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-danger-light)',
    color: 'var(--color-danger)',
    fontSize: '13px',
    fontWeight: 500,
  },
};

export default QuickPay;
